/* eslint-disable */
import React, { useState, useEffect } from "react";
import Select from "react-select";

/*import url and gateway methods */
import { getData, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

/*import helper functions */
import { getAutomationTaskScheduleTemplates } from "helper/AutomationHelper/AutomationHelper";

import { useTranslation } from "react-i18next";

// @dnd-kit imports for drag and drop
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

// ─────────────────────────────────────────────────────────────
// Token generator – random 32-char hex string
// ─────────────────────────────────────────────────────────────
const generateToken = () =>
  [...Array(32)]
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join("");

// ─────────────────────────────────────────────────────────────
// Factory – keeps default subtask shape in one place
// ─────────────────────────────────────────────────────────────
const getTodayStr = () => new Date().toISOString().split("T")[0];

const getDefaultTime = () => {
  const d = new Date();
  d.setHours(d.getHours() + 1);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const defaultSubtask = (step = 1) => ({
  id: Date.now() + Math.random(),
  name: "",
  description: "",
  step,
  startdate: getTodayStr(),
  starttime: getDefaultTime(),
  automation: {
    webhook_url: "",
    webhook_name: "",
    token: generateToken(),
    payload_template: "",
  },
});

// ─────────────────────────────────────────────────────────────
// Build the payload preview JSON shown to the user
// reactive  → full outbound payload (sent by us to n8n)
// proactive → task data payload (returned from our API to n8n's CRON)
// ─────────────────────────────────────────────────────────────
const buildPayloadPreview = (subtask, executionType, callbackUrl) => {
  const raw = subtask.automation?.payload_template || "";
  let extra = {};
  if (raw.trim()) {
    try { extra = JSON.parse(raw); } catch {}
  }

  if (executionType === "reactive") {
    return {
      callbackUrl: callbackUrl || "",
      taskid: "{{taskid}}",
      email: "{{email}}",
      name: "{{name}}",
      phone: "{{phone}}",
      token: subtask.automation?.token || "",
      webhook_name: subtask.automation?.webhook_name || "",
      ...extra,
    };
  }

  // proactive – what our API returns when n8n polls it
  return {
    taskid: "{{taskid}}",
    email: "{{email}}",
    name: "{{name}}",
    phone: "{{phone}}",
    ...extra,
  };
};

// ─────────────────────────────────────────────────────────────
// SortableSubtaskItem – one card per subtask
// ─────────────────────────────────────────────────────────────
const SortableSubtaskItem = ({
  subtask,
  index,
  subtasks,
  removeSubtask,
  updateSubtask,
  t,
  subtasksError,
  callbackUrl,
  executionType,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subtask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isReactive = executionType === "reactive";

  const [payloadJsonError, setPayloadJsonError] = useState("");

  const isIncomplete =
    subtasksError &&
    (!subtask.name.trim() ||
      !subtask.description.trim() ||
      (isReactive &&
        (!subtask.automation?.webhook_url?.trim() ||
          !subtask.automation?.token?.trim())));

  const handlePayloadChange = (value) => {
    updateSubtask(subtask.id, "automation.payload_template", value);
    if (value.trim()) {
      try {
        JSON.parse(value);
        setPayloadJsonError("");
      } catch {
        setPayloadJsonError(t("Invalid JSON format"));
      }
    } else {
      setPayloadJsonError("");
    }
  };

  const previewJson = JSON.stringify(
    buildPayloadPreview(subtask, executionType, callbackUrl),
    null,
    2
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`subtask-item mb-3 border rounded-3 bg-white ${
        isIncomplete ? "border-danger" : "border-gray-200"
      }`}
    >
      {/* ── Card header ── */}
      <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light rounded-top-3">
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light border-0 p-1 drag-handle"
            style={{ cursor: "grab" }}
            {...attributes}
            {...listeners}
          >
            <span className="material-symbols-outlined text-muted" style={{ fontSize: "18px" }}>
              drag_indicator
            </span>
          </button>
          <span className="badge bg-primary">#{index + 1}</span>
          <span className="badge bg-secondary">{t("Step")} {subtask.step || 0}</span>
          <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
            {isReactive ? t("Reactive") : t("Proactive")}
          </span>
        </div>

        {subtasks.length > 1 && (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-0"
            onClick={() => removeSubtask(subtask.id)}
          >
            <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>delete</span>
          </button>
        )}
      </div>

      {/* ── Card body ── */}
      <div className="p-3">
        {/* Name + Description */}
        <input
          type="text"
          className={`form-control mb-2 ${
            isIncomplete && !subtask.name.trim() ? "is-invalid" : ""
          }`}
          value={subtask.name}
          onChange={(e) => updateSubtask(subtask.id, "name", e.target.value)}
          placeholder={t("Subtask name")}
        />
        <textarea
          className={`form-control mb-3 ${
            isIncomplete && !subtask.description.trim() ? "is-invalid" : ""
          }`}
          rows="2"
          value={subtask.description}
          onChange={(e) => updateSubtask(subtask.id, "description", e.target.value)}
          placeholder={t("Subtask description")}
        />

        {/* Schedule Date / Schedule Time */}
        <div className="row g-2 mb-3">
          <div className="col-6">
            <label className="form-label fs-sm fw-semibold mb-1">{t("Schedule Date")}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={subtask.startdate || ""}
              onChange={(e) => updateSubtask(subtask.id, "startdate", e.target.value)}
            />
          </div>
          <div className="col-6">
            <label className="form-label fs-sm fw-semibold mb-1">{t("Schedule Time")}</label>
            <input
              type="time"
              className="form-control form-control-sm"
              value={subtask.starttime || ""}
              onChange={(e) => updateSubtask(subtask.id, "starttime", e.target.value)}
            />
          </div>
        </div>

        {/* ── Automation config ── */}
        <div className="bg-white border rounded-3 shadow-sm" style={{ borderColor: "#e0e0e0" }}>
          <div className="px-3 py-3 border-bottom" style={{ borderColor: "#f0f0f0" }}>
            <div className="d-flex align-items-center gap-2">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                settings_ethernet
              </span>
              <span className="fw-semibold text-dark">
                {isReactive ? t("Webhook Config") : t("Payload Config")}
              </span>
              {isReactive && (
                <small className="text-muted ms-1">
                  — {t("we call n8n's webhook with this payload")}
                </small>
              )}
              {!isReactive && (
                <small className="text-muted ms-1">
                  — {t("n8n polls our API and receives this data")}
                </small>
              )}
            </div>
          </div>

          <div className="row g-0">
            {/* ── Left: config fields ── */}
            <div className="col-xl-6 p-4 border-end" style={{ borderColor: "#f0f0f0" }}>
              {/* Reactive-only fields */}
              {isReactive && (
                <>
                  {/* Token */}
                  <div className="mb-3">
                    <label className="form-label fs-sm fw-semibold text-dark mb-2">
                      {t("Token")} <span className="text-danger">*</span>{" "}
                      <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>
                        {t("(auto-generated, editable)")}
                      </span>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control font-monospace ${
                          isIncomplete && !subtask.automation?.token?.trim() ? "is-invalid" : ""
                        }`}
                        value={subtask.automation?.token || ""}
                        onChange={(e) =>
                          updateSubtask(subtask.id, "automation.token", e.target.value)
                        }
                        placeholder={t("Verification token")}
                        style={{ fontSize: "12px" }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title={t("Regenerate token")}
                        onClick={() =>
                          updateSubtask(subtask.id, "automation.token", generateToken())
                        }
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>
                          refresh
                        </span>
                      </button>
                    </div>
                    <small className="text-muted d-block mt-1">
                      {t("n8n must return this token in its callback for verification.")}
                    </small>
                  </div>

                  {/* Webhook URL */}
                  <div className="mb-3">
                    <label className="form-label fs-sm fw-semibold text-dark mb-2">
                      {t("Webhook URL")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="url"
                      className={`form-control ${
                        isIncomplete && !subtask.automation?.webhook_url?.trim() ? "is-invalid" : ""
                      }`}
                      value={subtask.automation?.webhook_url || ""}
                      onChange={(e) =>
                        updateSubtask(subtask.id, "automation.webhook_url", e.target.value)
                      }
                      placeholder="https://your-n8n-instance.com/webhook/..."
                    />
                  </div>

                  {/* Webhook Name */}
                  <div className="mb-3">
                    <label className="form-label fs-sm fw-semibold text-dark mb-2">
                      {t("Webhook Name")}{" "}
                      <span className="text-muted fw-normal">{t("(optional)")}</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={subtask.automation?.webhook_name || ""}
                      onChange={(e) =>
                        updateSubtask(subtask.id, "automation.webhook_name", e.target.value)
                      }
                      placeholder={t("e.g. Send Welcome Email")}
                    />
                  </div>
                </>
              )}

              {/* Payload (both modes) */}
              <div>
                <label className="form-label fs-sm fw-semibold text-dark mb-2">
                  {isReactive ? t("Extra Payload Fields (JSON)") : t("Payload (JSON)")}{" "}
                  <span className="text-muted fw-normal">{t("(optional)")}</span>
                </label>
                <textarea
                  className={`form-control font-monospace ${payloadJsonError ? "is-invalid" : ""}`}
                  rows="6"
                  value={subtask.automation?.payload_template || ""}
                  onChange={(e) => handlePayloadChange(e.target.value)}
                  placeholder={`{\n  "custom_field": "value"\n}`}
                  style={{ fontSize: "12px", resize: "none" }}
                />
                {payloadJsonError ? (
                  <div className="invalid-feedback">{payloadJsonError}</div>
                ) : (
                  <small className="text-muted d-block mt-1">
                    {isReactive
                      ? t("Extra fields merged into the payload. Standard fields (callbackUrl, taskid, email, name, phone, token) are always included.")
                      : t("Fields that will be included in the task data returned to n8n's CRON. Standard fields (taskid, email, name, phone) are always included.")}
                  </small>
                )}
              </div>
            </div>

            {/* ── Right: live payload preview ── */}
            <div className="col-xl-6 p-4 d-flex flex-column" style={{ background: "#fafafa" }}>
              <div className="mb-2">
                <label className="form-label fs-sm fw-semibold d-flex align-items-center gap-2 mb-0">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "18px" }}>
                    preview
                  </span>
                  <span className="text-dark">{t("Payload Preview")}</span>
                  <span className="badge bg-primary ms-1" style={{ fontSize: "10px" }}>
                    {isReactive ? "POST" : "GET"}
                  </span>
                </label>
              </div>
              
              <div className="flex-grow-1 d-flex flex-column">
                <pre
                  className="rounded-3 p-3 mb-2 flex-grow-1"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e0e0e0",
                    color: "#2d3748",
                    fontSize: "11.5px",
                    lineHeight: "1.6",
                    minHeight: "280px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {previewJson}
                </pre>
                
                <div className="mb-3">
                  <small className="text-muted d-block">
                    <span
                      className="material-symbols-outlined align-middle me-1"
                      style={{ fontSize: "13px" }}
                    >
                      info
                    </span>
                    <code>{"{{taskid}}"}</code>{", "}
                    <code>{"{{email}}"}</code>{", "}
                    <code>{"{{name}}"}</code>{", "}
                    <code>{"{{phone}}"}</code>{" "}
                    {t("are replaced with real lead data.")}
                  </small>
                </div>

                {/* Created / Updated info */}
                <div className="p-2 bg-white rounded-2 border d-flex gap-3" style={{ borderColor: "#e0e0e0" }}>
                  <small className="text-muted">
                    <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: "13px" }}>
                      calendar_today
                    </span>
                    <strong>{t("Created")}:</strong> {t("auto on save")}
                  </small>
                  <small className="text-muted">
                    <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: "13px" }}>
                      update
                    </span>
                    <strong>{t("Updated")}:</strong> {t("auto on change")}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ScheduleAutomationPopup – main component
// ─────────────────────────────────────────────────────────────
const ScheduleAutomationPopup = ({
  selectedLeads,
  module = "lead",
  onClose,
  onSuccess,
  setShowAlert,
  setAlertMessage,
  setMessageType,
}) => {
  const userToken = localStorage.getItem("token");
  const { t } = useTranslation();

  // Callback URL shown in the preview panel
  const callbackUrl = `${url.API_BASE_URL}/automationtaskschedule/webhook/n8n/callback`;

  // Logged-in user email for the proactive API endpoint preview
  const userEmail = (() => {
    try {
      const raw =
        localStorage.getItem("userInfo") ||
        localStorage.getItem("userData") ||
        localStorage.getItem("userdata");
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.email || parsed.userEmail || localStorage.getItem("email") || "";
      }
      return localStorage.getItem("email") || "";
    } catch {
      return localStorage.getItem("email") || "";
    }
  })();

  const proactiveApiEndpoint = `${url.API_BASE_URL}/automationtaskschedule/proactive?email=${userEmail || "{user_email}"}`;

  // ── State ──────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creationMode, setCreationMode] = useState("custom"); // "template" | "custom"
  const [executionType, setExecutionType] = useState("reactive");
  const [automationSchedule, setAutomationSchedule] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Parent task
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");

  // Subtasks
  const [subtasks, setSubtasks] = useState([defaultSubtask(1)]);

  // Template saving
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [isPublic, setIsPublic] = useState(false);

  // Validation
  const [taskTitleError, setTaskTitleError] = useState("");
  const [taskDescriptionError, setTaskDescriptionError] = useState("");
  const [subtasksError, setSubtasksError] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");

  // Additional
  const [projectId, setProjectId] = useState("");
  const [assignedUser, setAssignedUser] = useState("");

  // ── Templates ──────────────────────────────────────────────
  const fetchTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const requestUrl = `${url.API_BASE_URL}${url.API_GET_AUTOMATION_TASK_SCHEDULE_TEMPLATES}?token=${userToken}`;
      const response = await getData(requestUrl);

      if (response.status) {
        setTemplates(response.data || []);
        setTemplateOptions(
          (response.data || []).map((tpl) => ({
            value: tpl._id || tpl.id,
            label: tpl.name,
            data: tpl,
          }))
        );
      } else {
        setAlertMessage(t("Failed to load templates"));
        setMessageType("danger");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      setAlertMessage(t("Error loading templates"));
      setMessageType("danger");
      setShowAlert(true);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // ── Subtask helpers ────────────────────────────────────────
  const addSubtask = () => {
    const nextStep =
      subtasks.length > 0
        ? Math.max(...subtasks.map((st) => st.step || 0)) + 1
        : 1;
    setSubtasks([...subtasks, defaultSubtask(nextStep)]);
  };

  const removeSubtask = (id) => {
    if (subtasks.length > 1) {
      setSubtasks(subtasks.filter((st) => st.id !== id));
    }
  };

  // Handles flat fields ("name", "description", "type") and nested
  // automation fields using dot-notation ("automation.webhook_url", etc.)
  const updateSubtask = (id, field, value) => {
    setSubtasks((prev) =>
      prev.map((st) => {
        if (st.id !== id) return st;
        if (field.startsWith("automation.")) {
          const key = field.slice("automation.".length);
          return {
            ...st,
            automation: { ...st.automation, [key]: value },
          };
        }
        return { ...st, [field]: value };
      })
    );
    if (subtasksError) setSubtasksError("");
  };

  // ── Drag & drop ────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setSubtasks((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex).map((item, idx) => ({
          ...item,
          step: idx + 1,
        }));
      });
    }
  };

  // ── Validation helpers ─────────────────────────────────────
  const isValidJson = (str) => {
    if (!str || !str.trim()) return true;
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  const parsePayload = (str) => {
    if (!str || !str.trim()) return {};
    try {
      return JSON.parse(str);
    } catch {
      return {};
    }
  };

  // ── Submit ─────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    setTaskTitleError("");
    setTaskDescriptionError("");
    setSubtasksError("");
    setTemplateError("");
    setTemplateNameError("");

    let hasError = false;

    if (creationMode === "template" && !selectedTemplate) {
      setTemplateError(t("Please select a template"));
      hasError = true;
    }

    if (creationMode === "custom") {
      if (!taskTitle.trim()) {
        setTaskTitleError(t("Please enter task title"));
        hasError = true;
      }

      if (!taskDescription.trim()) {
        setTaskDescriptionError(t("Please enter task description"));
        hasError = true;
      }

      const validSubtasks = subtasks.filter(
        (st) => st.name.trim() && st.description.trim()
      );
      if (validSubtasks.length === 0) {
        setSubtasksError(
          t("Please add at least one subtask with both title and description filled.")
        );
        hasError = true;
      }

      if (!hasError) {
        for (const st of validSubtasks) {
          if (executionType === "reactive") {
            if (!st.automation?.webhook_url?.trim()) {
              setSubtasksError(t("All subtasks must have a Webhook URL filled."));
              hasError = true;
              break;
            }
            if (!st.automation?.token?.trim()) {
              setSubtasksError(t("All subtasks must have a Token filled."));
              hasError = true;
              break;
            }
          }
          if (!isValidJson(st.automation?.payload_template)) {
            setSubtasksError(t("One or more subtasks have an invalid JSON payload."));
            hasError = true;
            break;
          }
        }
      }

      if (saveAsTemplate && !templateName.trim()) {
        setTemplateNameError(t("Please enter template name to save"));
        hasError = true;
      }
    }

    if (hasError) return;

    if (!selectedLeads || selectedLeads.length === 0) {
      setAlertMessage(t("No leads selected"));
      setMessageType("danger");
      setShowAlert(true);
      return;
    }

    try {
      setIsLoading(true);

      const isBulk = selectedLeads.length > 1;

      const mappedSubtasks =
        creationMode === "custom"
          ? subtasks
              .filter((st) => st.name.trim())
              .map((st, idx) => ({
                title: st.name,
                description: st.description,
                type: "automation",
                order: st.step || idx + 1,
                step: st.step || idx + 1,
                startdate: st.startdate || "",
                enddate: st.startdate || "", // enddate mirrors startdate
                starttime: st.starttime || "",
                automation: {
                  provider: "n8n",
                  webhook_url: st.automation.webhook_url || "",
                  webhook_name: st.automation.webhook_name || "",
                  token: st.automation.token || "",
                  payload_template: parsePayload(st.automation.payload_template),
                },
              }))
          : [];

      const requestBody = {
        tasktitle: creationMode === "custom" ? taskTitle : "",
        taskdescription: creationMode === "custom" ? taskDescription : "",
        subtasks: mappedSubtasks,
        executiontype: executionType,
        automationschedule: automationSchedule || null,
        projectid: projectId || null,
        assigneduser: assignedUser || null,
        saveastemplate: saveAsTemplate,
        templatename: saveAsTemplate ? templateName : "",
        ispublic: saveAsTemplate ? isPublic : false,
      };

      if (isBulk) {
        requestBody.leadids = selectedLeads.map((lead) => lead._id || lead.id);
      } else {
        requestBody.leadid = selectedLeads[0]._id || selectedLeads[0].id;
      }

      let requestUrl;
      if (creationMode === "template") {
        requestBody.templateid = selectedTemplate.value;
        requestUrl = isBulk
          ? `${url.API_BASE_URL}${url.API_BULK_CREATE_AUTOMATION_TASK_SCHEDULE_TEMPLATE}?token=${userToken}`
          : `${url.API_BASE_URL}${url.API_CREATE_AUTOMATION_TASK_SCHEDULE_TEMPLATE}?token=${userToken}`;
      } else {
        requestUrl = isBulk
          ? `${url.API_BASE_URL}${url.API_BULK_CREATE_AUTOMATION_TASK_SCHEDULE}?token=${userToken}`
          : `${url.API_BASE_URL}${url.API_CREATE_AUTOMATION_TASK_SCHEDULE}?token=${userToken}`;
      }

      console.log("Request URL:", requestUrl);
      console.log("Request Body:", JSON.stringify(requestBody, null, 2));

      const response = await postData(requestUrl, requestBody);

      if (response.status) {
        setAlertMessage(t("Automation task created successfully"));
        setMessageType("success");
        setShowAlert(true);
        resetForm();

        const offcanvasEl = document.getElementById("offcanvasScheduleAutomation");
        if (offcanvasEl) {
          const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasEl);
          if (bsOffcanvas) bsOffcanvas.hide();
        }

        if (onSuccess) onSuccess(response.data);
      } else {
        setAlertMessage(response.message || t("Failed to create automation task"));
        setMessageType("danger");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error creating automation task:", error);
      setAlertMessage(t("Error creating automation task"));
      setMessageType("danger");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset ──────────────────────────────────────────────────
  const resetForm = () => {
    setSelectedTemplate(null);
    setCreationMode("custom");
    setExecutionType("reactive");
    setAutomationSchedule("");
    setTaskTitle("");
    setTaskDescription("");
    setSubtasks([defaultSubtask(1)]);
    setSaveAsTemplate(false);
    setTemplateName("");
    setIsPublic(false);
    setProjectId("");
    setAssignedUser("");
    setTaskTitleError("");
    setTaskDescriptionError("");
    setSubtasksError("");
    setTemplateError("");
    setTemplateNameError("");
  };

  const handleClose = () => {
    resetForm();
    if (onClose) onClose();
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="offcanvasScheduleAutomation"
      aria-labelledby="offcanvasScheduleAutomationLabel"
    >
      {/* Header */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              schedule
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasScheduleAutomationLabel"
            >
              {t("Schedule Automation")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedLeads?.length || 0} {t(`${module}s selected`)}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={handleClose}
        />
      </div>

      {/* Body */}
      <div className="offcanvas-body p-4">
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
            <div className="fields-container flex-fill">

              {/* ── 1. Creation Mode ── */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                  {t("Creation Mode")} <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="creationMode"
                      id="modeCustom"
                      value="custom"
                      checked={creationMode === "custom"}
                      onChange={(e) => setCreationMode(e.target.value)}
                    />
                    <label className="form-check-label fw-medium" htmlFor="modeCustom">
                      {t("Create Custom")}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="creationMode"
                      id="modeTemplate"
                      value="template"
                      checked={creationMode === "template"}
                      onChange={(e) => setCreationMode(e.target.value)}
                    />
                    <label className="form-check-label fw-medium" htmlFor="modeTemplate">
                      {t("Use Template")}
                    </label>
                  </div>
                </div>
              </div>

              {/* ── 2. Execution Type ── */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                  {t("Execution Type")} <span className="text-danger">*</span>
                </label>

                {creationMode === "template" ? (
                  /* Read-only when using a template – value comes from the template */
                  <div className="d-flex align-items-center gap-2">
                    {selectedTemplate?.data?.executiontype ? (
                      <>
                        <span
                          className={`badge px-3 py-2 ${
                            selectedTemplate.data.executiontype === "reactive"
                              ? "bg-info text-dark"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {selectedTemplate.data.executiontype === "reactive"
                            ? t("Reactive")
                            : t("Proactive")}
                        </span>
                        <small className="text-muted">
                          <span
                            className="material-symbols-outlined align-middle me-1"
                            style={{ fontSize: "14px" }}
                          >
                            lock
                          </span>
                          {t("Execution type is set by the selected template.")}
                        </small>
                      </>
                    ) : (
                      <small className="text-muted fst-italic">
                        {t("Select a template to see its execution type.")}
                      </small>
                    )}
                  </div>
                ) : (
                  /* Editable radio buttons for custom creation */
                  <div className="d-flex flex-column gap-2">
                    {[
                      {
                        value: "reactive",
                        label: t("Reactive"),
                        hint: t("Trigger payload to webhook url on schedule time / when task is created"),
                      },
                      {
                        value: "proactive",
                        label: t("Proactive"),
                        hint: t("These task will be available in a API endpoint. A CRON like from N8N will execute the task on schedule time"),
                      },
                    ].map(({ value, label, hint }) => (
                      <div className="form-check" key={value}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="executionType"
                          id={`executionType-${value}`}
                          value={value}
                          checked={executionType === value}
                          onChange={(e) => setExecutionType(e.target.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`executionType-${value}`}
                        >
                          <span className="fw-medium">{label}</span>
                          <small className="d-block text-muted">{hint}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Proactive API endpoint info ── */}
              {executionType === "proactive" && (
                <div className="form-group mb-4 p-3 border border-warning rounded-8 shadow-sm bg-warning bg-opacity-10">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-warning" style={{ fontSize: "20px" }}>
                      api
                    </span>
                    <h6 className="mb-0 fw-semibold text-dark">{t("Proactive Task API Endpoint")}</h6>
                    <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>GET</span>
                  </div>
                  <p className="text-muted fs-sm mb-2">
                    {t("Configure n8n's HTTP Request node (CRON) to poll this endpoint to fetch all pending proactive tasks for the logged-in user:")}
                  </p>
                  <div className="d-flex align-items-center gap-2">
                    <code
                      className="flex-grow-1 p-2 rounded-2 border"
                      style={{
                        background: "#1e1e2e",
                        color: "#cdd6f4",
                        fontSize: "12px",
                        wordBreak: "break-all",
                        display: "block",
                        lineHeight: "1.6",
                      }}
                    >
                      {proactiveApiEndpoint}
                    </code>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      title={t("Copy URL")}
                      onClick={() => navigator.clipboard?.writeText(proactiveApiEndpoint)}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>content_copy</span>
                    </button>
                  </div>
                  {!userEmail && (
                    <small className="text-danger d-block mt-2">
                      <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: "13px" }}>warning</span>
                      {t("User email not found in session. Replace {user_email} with the actual email address.")}
                    </small>
                  )}
                </div>
              )}

              {/* ── 3. Template Selection ── */}
              {creationMode === "template" && (
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    {t("Automation Template")} <span className="text-danger">*</span>
                  </label>
                  {isLoadingTemplates ? (
                    <div className="d-flex align-items-center gap-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                      </div>
                      <span className="text-muted">{t("Loading templates...")}</span>
                    </div>
                  ) : (
                    <>
                      <Select
                        placeholder={t("Select a template")}
                        options={templateOptions}
                        value={selectedTemplate}
                        onChange={(selected) => {
                          setSelectedTemplate(selected);
                          setTemplateError("");
                          // Sync execution type from template
                          if (selected?.data?.executiontype) {
                            setExecutionType(selected.data.executiontype);
                          } else {
                            setExecutionType("reactive");
                          }
                        }}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: templateError ? "#dc3545" : base.borderColor,
                            "&:hover": {
                              borderColor: templateError ? "#dc3545" : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {templateError && (
                        <div className="text-danger mt-2 fs-sm">
                          <span
                            className="material-symbols-outlined align-middle me-1"
                            style={{ fontSize: "16px" }}
                          >
                            error
                          </span>
                          {templateError}
                        </div>
                      )}
                    </>
                  )}

                  {selectedTemplate?.data && (
                    <div className="mt-3 p-3 bg-light rounded-3 border shadow-sm">
                      {/* Overview */}
                      <div className="mb-3 p-3 bg-white rounded-3 border border-info">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-info" style={{ fontSize: "24px" }}>
                            info
                          </span>
                          <h6 className="mb-0 fw-bold text-dark">{t("Template Overview")}</h6>
                        </div>
                        <p className="mb-0 text-muted fs-sm">
                          {selectedTemplate.data?.subtaskblueprint?.length || 0}{" "}
                          {t("subtasks will be created for each of the")}{" "}
                          {selectedLeads?.length || 0} {t(`selected ${module}s`)}
                          <span className="d-block fw-bold text-primary mt-1">
                            {t("Total subtasks to create")}:{" "}
                            {(selectedTemplate.data?.subtaskblueprint?.length || 0) *
                              (selectedLeads?.length || 0)}
                          </span>
                        </p>
                      </div>

                      {/* Parent task */}
                      {selectedTemplate.data?.taskblueprint && (
                        <div className="mb-3 p-3 bg-white rounded-3 border border-primary shadow-sm">
                          <div className="d-flex align-items-start gap-3">
                            <div className="icon-wrapper p-2 bg-primary bg-opacity-10 rounded-circle">
                              <span
                                className="material-symbols-outlined text-primary"
                                style={{ fontSize: "24px" }}
                              >
                                account_tree
                              </span>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <span className="badge bg-primary">{t("Parent Task")}</span>
                                <h5 className="mb-0 fw-bold text-dark">
                                  {selectedTemplate.data.taskblueprint.title}
                                </h5>
                              </div>
                              {selectedTemplate.data.taskblueprint.description && (
                                <div className="p-2 bg-light rounded-2 border">
                                  <p className="mb-0 text-muted fs-sm">
                                    <span
                                      className="material-symbols-outlined align-middle me-1"
                                      style={{ fontSize: "16px" }}
                                    >
                                      description
                                    </span>
                                    {selectedTemplate.data.taskblueprint.description}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Child tasks – full read-only view */}
                      {selectedTemplate.data?.subtaskblueprint?.length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex align-items-center gap-2 mb-3 p-2 bg-white rounded-2 border">
                            <span className="material-symbols-outlined text-secondary" style={{ fontSize: "20px" }}>
                              list_alt
                            </span>
                            <h6 className="mb-0 fw-bold text-dark">{t("Subtasks")}</h6>
                            <span className="badge bg-secondary ms-auto">
                              {selectedTemplate.data.subtaskblueprint.length} {t("tasks")}
                            </span>
                          </div>

                          {[...selectedTemplate.data.subtaskblueprint]
                            .sort((a, b) => (a.step || 0) - (b.step || 0))
                            .map((st, idx) => {
                              const tplExecType = selectedTemplate.data.executiontype || "reactive";
                              const isReactiveSt = tplExecType === "reactive";
                              const payloadStr =
                                typeof st.automation?.payload_template === "object"
                                  ? JSON.stringify(st.automation?.payload_template || {})
                                  : (st.automation?.payload_template || "");
                              const previewData = buildPayloadPreview(
                                { automation: { ...st.automation, payload_template: payloadStr } },
                                tplExecType,
                                callbackUrl
                              );

                              return (
                                <div
                                  key={st._id || idx}
                                  className="mb-3 border rounded-3 bg-white shadow-sm overflow-hidden"
                                >
                                  {/* subtask header */}
                                  <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
                                    <span className="badge bg-primary">#{idx + 1}</span>
                                    <span className="badge bg-secondary">{t("Step")} {st.step || idx + 1}</span>
                                    <span className={`badge ${isReactiveSt ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                                      {isReactiveSt ? t("Reactive") : t("Proactive")}
                                    </span>
                                    <span className="fw-semibold text-dark ms-1">{st.title}</span>
                                  </div>

                                  <div className="p-3">
                                    {/* description */}
                                    {st.description && (
                                      <p className="text-muted fs-sm mb-3">{st.description}</p>
                                    )}

                                    {/* automation config – read-only */}
                                    <div className="border rounded-3 overflow-hidden">
                                      <div className="px-3 py-2 bg-light border-bottom d-flex align-items-center gap-2">
                                        <span className="material-symbols-outlined text-secondary" style={{ fontSize: "16px" }}>
                                          settings_ethernet
                                        </span>
                                        <span className="fw-semibold fs-sm text-dark">
                                          {isReactiveSt ? t("Webhook Config") : t("Payload Config")}
                                        </span>
                                        <span className="badge bg-secondary ms-auto" style={{ fontSize: "10px" }}>
                                          {t("read-only")}
                                        </span>
                                      </div>

                                      <div className="row g-0">
                                        {/* left: fields */}
                                        <div className="col-xl-6 p-3 border-end">
                                          {isReactiveSt && (
                                            <>
                                              {/* Token */}
                                              <div className="mb-3">
                                                <label className="form-label fs-sm fw-semibold text-dark mb-1">
                                                  {t("Token")}
                                                </label>
                                                <input
                                                  type="text"
                                                  className="form-control form-control-sm font-monospace bg-light"
                                                  value={st.automation?.token || "—"}
                                                  readOnly
                                                  style={{ fontSize: "11px", color: "#555" }}
                                                />
                                              </div>
                                              {/* Webhook URL */}
                                              <div className="mb-3">
                                                <label className="form-label fs-sm fw-semibold text-dark mb-1">
                                                  {t("Webhook URL")}
                                                </label>
                                                <input
                                                  type="text"
                                                  className="form-control form-control-sm bg-light"
                                                  value={st.automation?.webhook_url || "—"}
                                                  readOnly
                                                  style={{ fontSize: "12px", color: "#555" }}
                                                />
                                              </div>
                                              {/* Webhook Name */}
                                              {st.automation?.webhook_name && (
                                                <div className="mb-3">
                                                  <label className="form-label fs-sm fw-semibold text-dark mb-1">
                                                    {t("Webhook Name")}
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control form-control-sm bg-light"
                                                    value={st.automation.webhook_name}
                                                    readOnly
                                                    style={{ fontSize: "12px", color: "#555" }}
                                                  />
                                                </div>
                                              )}
                                            </>
                                          )}
                                          {/* Payload */}
                                          <div>
                                            <label className="form-label fs-sm fw-semibold text-dark mb-1">
                                              {isReactiveSt ? t("Extra Payload Fields") : t("Payload")}
                                            </label>
                                            {payloadStr && payloadStr !== "{}" ? (
                                              <pre
                                                className="rounded-2 p-2 mb-0 bg-light font-monospace"
                                                style={{ fontSize: "11px", color: "#555", maxHeight: "120px", overflowY: "auto" }}
                                              >
                                                {typeof st.automation?.payload_template === "object"
                                                  ? JSON.stringify(st.automation?.payload_template, null, 2)
                                                  : (st.automation?.payload_template || "")}
                                              </pre>
                                            ) : (
                                              <p className="text-muted fs-sm mb-0 fst-italic">{t("No extra fields")}</p>
                                            )}
                                          </div>
                                        </div>

                                        {/* right: payload preview */}
                                        <div className="col-xl-6 p-3" style={{ background: "#fafafa" }}>
                                          <label className="form-label fs-sm fw-semibold d-flex align-items-center gap-2 mb-2">
                                            <span className="material-symbols-outlined text-primary" style={{ fontSize: "15px" }}>preview</span>
                                            {t("Payload Preview")}
                                            <span className="badge bg-primary ms-1" style={{ fontSize: "10px" }}>
                                              {isReactiveSt ? "POST" : "GET"}
                                            </span>
                                          </label>
                                          <pre
                                            className="rounded-2 p-2 mb-0"
                                            style={{
                                              background: "#1e1e2e",
                                              color: "#cdd6f4",
                                              fontSize: "11px",
                                              lineHeight: "1.6",
                                              maxHeight: "220px",
                                              overflowY: "auto",
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-all",
                                            }}
                                          >
                                            {JSON.stringify(previewData, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── 5. Custom Task Creation ── */}
              {creationMode === "custom" && (
                <>
                  {/* Task Title */}
                  <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      {t("Task Title")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${taskTitleError ? "is-invalid" : ""}`}
                      value={taskTitle}
                      onChange={(e) => {
                        setTaskTitle(e.target.value);
                        setTaskTitleError("");
                      }}
                      placeholder={t("Enter task title")}
                    />
                    {taskTitleError && (
                      <div className="text-danger mt-2 fs-sm">
                        <span
                          className="material-symbols-outlined align-middle me-1"
                          style={{ fontSize: "16px" }}
                        >
                          error
                        </span>
                        {taskTitleError}
                      </div>
                    )}
                  </div>

                  {/* Task Description */}
                  <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      {t("Task Description")} <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${taskDescriptionError ? "is-invalid" : ""}`}
                      rows="3"
                      value={taskDescription}
                      onChange={(e) => {
                        setTaskDescription(e.target.value);
                        setTaskDescriptionError("");
                      }}
                      placeholder={t("Enter task description")}
                    />
                    {taskDescriptionError && (
                      <div className="text-danger mt-2 fs-sm">
                        <span
                          className="material-symbols-outlined align-middle me-1"
                          style={{ fontSize: "16px" }}
                        >
                          error
                        </span>
                        {taskDescriptionError}
                      </div>
                    )}
                  </div>

                  {/* ── Subtasks ── */}
                  <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <label className="d-block fs-sm fw-semibold mb-0 text-dark">
                          {t("Subtasks")} <span className="text-danger">*</span>
                        </label>
                        <small className="text-muted">
                          {t(
                            "Drag to reorder. Each subtask can be manual or trigger an n8n workflow."
                          )}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                        onClick={addSubtask}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>
                          add
                        </span>
                        <span>{t("Add Subtask")}</span>
                      </button>
                    </div>

                    {/* Info */}
                    <div
                      className="alert alert-info d-flex align-items-start gap-2 mb-3"
                      role="alert"
                    >
                      <span
                        className="material-symbols-outlined text-primary"
                        style={{ fontSize: "20px" }}
                      >
                        info
                      </span>
                      <div className="flex-grow-1">
                        <small className="d-block mb-1">
                          <strong>{t("How it works")}:</strong>{" "}
                          {t("Each subtask will be assigned to each of the")}{" "}
                          <strong>
                            {selectedLeads?.length || 0} {t(`selected ${module}s`)}
                          </strong>
                          .
                        </small>
                        <small className="d-block mb-1 text-danger">
                          <strong>{t("Note")}:</strong>{" "}
                          {t(
                            "At least one subtask must have both title and description filled."
                          )}
                        </small>
                        <small className="text-muted">
                          {t("Total subtasks to be created")}:{" "}
                          <strong className="text-primary">
                            {
                              subtasks.filter(
                                (st) => st.name.trim() && st.description.trim()
                              ).length
                            }{" "}
                            × {selectedLeads?.length || 0} ={" "}
                            {subtasks.filter(
                              (st) => st.name.trim() && st.description.trim()
                            ).length * (selectedLeads?.length || 0)}
                          </strong>
                        </small>
                      </div>
                    </div>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={subtasks.map((st) => st.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="subtasks-list">
                          {subtasks.map((subtask, index) => (
                            <SortableSubtaskItem
                              key={subtask.id}
                              subtask={subtask}
                              index={index}
                              subtasks={subtasks}
                              removeSubtask={removeSubtask}
                              updateSubtask={updateSubtask}
                              t={t}
                              subtasksError={subtasksError}
                              callbackUrl={callbackUrl}
                              executionType={executionType}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>

                    {subtasksError && (
                      <div className="text-danger d-flex align-items-start gap-2 mt-3">
                        <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>
                          error
                        </span>
                        <div className="flex-grow-1">{subtasksError}</div>
                      </div>
                    )}
                  </div>

                  {/* Save as Template */}
                  <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="saveAsTemplate"
                        checked={saveAsTemplate}
                        onChange={(e) => setSaveAsTemplate(e.target.checked)}
                      />
                      <label
                        className="form-check-label fw-medium ms-2"
                        htmlFor="saveAsTemplate"
                      >
                        {t("Save as template for future use")}
                      </label>
                    </div>
                    {saveAsTemplate && (
                      <>
                        <input
                          type="text"
                          className={`form-control mb-3 ${
                            templateNameError ? "is-invalid" : ""
                          }`}
                          value={templateName}
                          onChange={(e) => {
                            setTemplateName(e.target.value);
                            setTemplateNameError("");
                          }}
                          placeholder={t("Enter template name")}
                        />
                        {templateNameError && (
                          <div className="text-danger mb-3 fs-sm">
                            <span
                              className="material-symbols-outlined align-middle me-1"
                              style={{ fontSize: "16px" }}
                            >
                              error
                            </span>
                            {templateNameError}
                          </div>
                        )}
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isPublic"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                          />
                          <label className="form-check-label ms-2" htmlFor="isPublic">
                            {t("Is public")}
                          </label>
                          <small className="d-block text-muted ms-4">
                            {t("Make this template available to all users")}
                          </small>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

            </div>

            {/* ── Footer ── */}
            <div className="form-footer mt-4 pt-3 border-top d-flex justify-content-between align-items-center gap-3 sticky-bottom bg-light">
              <div className="flex-grow-1">
                {(taskTitleError ||
                  taskDescriptionError ||
                  subtasksError ||
                  templateError ||
                  templateNameError) && (
                  <div className="text-danger d-flex align-items-center gap-2">
                    <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>
                      error
                    </span>
                    <span className="fs-sm">
                      {t("Please fix the validation errors above")}
                    </span>
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-bs-dismiss="offcanvas"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  disabled={
                    isLoading ||
                    (creationMode === "template" && !selectedTemplate) ||
                    (creationMode === "custom" && !taskTitle.trim())
                  }
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>{t("Creating...")}</span>
                    </>
                  ) : (
                    <>
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "18px" }}
                      >
                        add_task
                      </span>
                      <span>{t("Create Task")}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleAutomationPopup;
